import React, { useState, useCallback } from 'react';
import { useFetch } from '../../hooks/useFetch';
import { platformAPI } from '../../api/client';
import { Card, Badge, Modal, Alert, Btn, FormInput, FormSelect, Spinner, EmptyState, Avatar, fmtDate } from '../../components/UI';

const STATUS_OPTS = [
  { value: 'submitted',    label: '📬 Submitted — Awaiting review' },
  { value: 'under_review', label: '🔍 Under Review — Being processed' },
  { value: 'active',       label: '✅ Active — Earning' },
  { value: 'paused',       label: '⏸ Paused — Temporarily stopped' },
  { value: 'rejected',     label: '❌ Rejected — Cannot activate' },
];

const STATUS_FILTERS = [
  { value: '',            label: 'All' },
  { value: 'submitted',   label: 'Submitted' },
  { value: 'under_review',label: 'Under Review' },
  { value: 'active',      label: 'Active' },
  { value: 'paused',      label: 'Paused' },
  { value: 'rejected',    label: 'Rejected' },
];

export default function AdminPlatformAccounts() {
  const [search,      setSearch]      = useState('');
  const [page,        setPage]        = useState(1);
  const [statusFilter, setStatusFilter] = useState('');
  const [viewItem,    setViewItem]    = useState(null);
  const [credentials, setCredentials] = useState(null);
  const [loadingCreds, setLoadingCreds] = useState(false);
  const [credsError,   setCredsError]   = useState('');
  const [statusForm,   setStatusForm]   = useState({ status: '', admin_note: '' });
  const [saving,       setSaving]       = useState(false);
  const [modalError,   setModalError]   = useState('');
  const [success,      setSuccess]      = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const fetchAccounts = useCallback(() => platformAPI.adminAll(page, search, statusFilter), [page, search, statusFilter]);
  const { data, loading, error, refetch } = useFetch(fetchAccounts);
  const accounts   = data?.accounts   || [];
  const pagination = data?.pagination || {};

  function openView(acct) {
    setViewItem(acct);
    setCredentials(null);
    setCredsError('');
    setShowPassword(false);
    setStatusForm({ status: acct.status, admin_note: acct.admin_note || '' });
    setModalError('');
  }

  async function loadCredentials() {
    if (!viewItem) return;
    setLoadingCreds(true); setCredsError('');
    try {
      const { data: res } = await platformAPI.adminCredentials(viewItem.id);
      setCredentials(res.account);
    } catch (err) {
      setCredsError(err.response?.data?.error || 'Failed to load credentials.');
    } finally { setLoadingCreds(false); }
  }

  async function saveStatus(e) {
    e.preventDefault();
    setSaving(true); setModalError('');
    try {
      await platformAPI.adminSetStatus(viewItem.id, statusForm);
      setSuccess(`Status updated to "${statusForm.status}" for ${viewItem.user_name}.`);
      setViewItem(null);
      refetch();
    } catch (err) {
      setModalError(err.response?.data?.error || 'Failed to update status.');
    } finally { setSaving(false); }
  }

  const PLATFORM_COLORS = {
    'Appen':'#ff6b35','Scale AI':'#4f6ef7','Remotasks':'#16a34a','Toloka':'#f4a61d',
    'Prolific':'#7c3aed','Outlier AI':'#0891b2','DataAnnotation':'#dc2626',
    'Clickworker':'#0f766e','iSoftStone':'#1d4ed8','Lionbridge':'#9333ea',
    'Hive Microtasks':'#b45309','TELUS AI':'#15803d',
  };

  const STATUS_COLOR = {
    submitted: '#d97706', under_review: '#4f6ef7',
    active: '#16a34a', paused: '#9099b4', rejected: '#dc2626',
  };

  return (
    <div style={styles.wrap}>
      <div style={styles.header}>
        <div>
          <h1 style={styles.h1}>Platform Account Credentials</h1>
          <p style={styles.sub}>Review all submitted AI platform accounts. View credentials, update status, and manage the team's workload.</p>
        </div>
      </div>

      {success && <Alert type="success" message={success} />}

      {/* Summary bar */}
      <div style={styles.summaryBar}>
        {[['submitted','📬','Submitted'],['under_review','🔍','In Review'],['active','✅','Active'],['paused','⏸','Paused'],['rejected','❌','Rejected']].map(([s, icon, label]) => {
          const count = accounts.filter(a => a.status === s).length;
          return (
            <div key={s} onClick={() => setStatusFilter(statusFilter === s ? '' : s)}
              style={{ ...styles.summaryChip, cursor: 'pointer', background: statusFilter === s ? STATUS_COLOR[s] + '18' : '#f8f9fc', borderColor: statusFilter === s ? STATUS_COLOR[s] : '#e2e6f0', color: statusFilter === s ? STATUS_COLOR[s] : '#636b89' }}>
              <span>{icon}</span>
              <span style={{ fontWeight: 700 }}>{count}</span>
              <span style={{ fontSize: 12 }}>{label}</span>
            </div>
          );
        })}
      </div>

      <Card>
        {/* Filters */}
        <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap', alignItems: 'center' }}>
          <input
            placeholder="Search user, email, or platform…"
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }}
            style={{ flex: 1, minWidth: 220, padding: '10px 14px', border: '1.5px solid #e2e6f0', borderRadius: 10, fontSize: 14, outline: 'none' }}
          />
          <div style={{ display: 'flex', gap: 6 }}>
            {STATUS_FILTERS.map(f => (
              <button key={f.value} onClick={() => { setStatusFilter(f.value); setPage(1); }}
                style={{ ...styles.filterBtn, ...(statusFilter === f.value ? styles.filterActive : {}) }}>
                {f.label}
              </button>
            ))}
          </div>
        </div>

        {loading ? <Spinner /> : error ? <p style={{ color: '#dc2626' }}>{error}</p>
          : accounts.length === 0 ? <EmptyState icon="🔐" message="No platform accounts found." />
          : (
            <>
              <div style={{ overflowX: 'auto' }}>
                <table style={styles.table}>
                  <thead>
                    <tr>
                      {['User','Platform','Account Email','Username','Status','Submitted','Actions'].map(h => (
                        <th key={h} style={styles.th}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {accounts.map(a => (
                      <tr key={a.id} style={styles.tr}>
                        <td style={styles.td}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <Avatar name={a.user_name} size={28} />
                            <div>
                              <div style={{ fontSize: 13, fontWeight: 600, color: '#1a1f5e' }}>{a.user_name}</div>
                              <div style={{ fontSize: 11, color: '#9099b4' }}>{a.user_email}</div>
                            </div>
                          </div>
                        </td>
                        <td style={styles.td}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <div style={{ width: 28, height: 28, borderRadius: 6, background: PLATFORM_COLORS[a.platform_name] || '#9099b4', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 10, fontWeight: 800, flexShrink: 0 }}>
                              {a.platform_name.slice(0, 2).toUpperCase()}
                            </div>
                            <span style={{ fontSize: 13, fontWeight: 600, color: '#1a1f5e' }}>{a.platform_name}</span>
                          </div>
                        </td>
                        <td style={styles.td}><span style={{ fontSize: 13, color: '#4f6ef7' }}>{a.account_email}</span></td>
                        <td style={styles.td}><span style={{ fontSize: 13, color: '#636b89' }}>{a.account_username || '—'}</span></td>
                        <td style={styles.td}><Badge status={a.status} /></td>
                        <td style={styles.td}><span style={{ fontSize: 12, color: '#9099b4' }}>{fmtDate(a.created_at)}</span></td>
                        <td style={styles.td}>
                          <Btn variant="ghost" style={{ fontSize: 12, padding: '5px 12px' }} onClick={() => openView(a)}>
                            🔐 View & Manage
                          </Btn>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {pagination.total > pagination.limit && (
                <div style={styles.pagination}>
                  <button style={styles.pageBtn} disabled={page === 1} onClick={() => setPage(p => p - 1)}>← Prev</button>
                  <span style={{ fontSize: 13, color: '#636b89' }}>Page {page} of {Math.ceil(pagination.total / pagination.limit)}</span>
                  <button style={styles.pageBtn} disabled={page * pagination.limit >= pagination.total} onClick={() => setPage(p => p + 1)}>Next →</button>
                </div>
              )}
            </>
          )
        }
      </Card>

      {/* View / Manage Modal */}
      <Modal open={!!viewItem} onClose={() => { setViewItem(null); setCredentials(null); }} title="Platform Account Details">
        {viewItem && (
          <>
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, background: '#f8f9fc', borderRadius: 12, padding: '14px 16px', marginBottom: 20 }}>
              <div style={{ width: 40, height: 40, borderRadius: 10, background: PLATFORM_COLORS[viewItem.platform_name] || '#9099b4', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 14, fontWeight: 800 }}>
                {viewItem.platform_name.slice(0, 2).toUpperCase()}
              </div>
              <div>
                <div style={{ fontWeight: 700, fontSize: 15, color: '#1a1f5e' }}>{viewItem.platform_name}</div>
                <div style={{ fontSize: 12, color: '#9099b4' }}>{viewItem.user_name} · {viewItem.user_email}</div>
              </div>
              <div style={{ marginLeft: 'auto' }}><Badge status={viewItem.status} /></div>
            </div>

            {/* Account details */}
            <div style={styles.detailsGrid}>
              <DetailRow label="Account Email"   value={viewItem.account_email} />
              <DetailRow label="Username"         value={viewItem.account_username || '—'} />
              <DetailRow label="Country"          value={viewItem.country || '—'} />
              <DetailRow label="Submitted"        value={fmtDate(viewItem.created_at)} />
              {viewItem.extra_notes && <DetailRow label="User Notes" value={viewItem.extra_notes} fullWidth />}
            </div>

            {/* Credentials section */}
            <div style={styles.credsSection}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                <strong style={{ fontSize: 14, color: '#1a1f5e' }}>🔑 Login Credentials</strong>
                {!credentials && (
                  <Btn variant="outline" style={{ fontSize: 12, padding: '6px 14px' }} loading={loadingCreds} onClick={loadCredentials}>
                    Reveal Credentials
                  </Btn>
                )}
              </div>
              {credsError && <Alert type="error" message={credsError} />}
              {credentials ? (
                <div style={styles.credBox}>
                  <div style={styles.credRow}>
                    <span style={styles.credLabel}>Email</span>
                    <span style={styles.credValue}>{credentials.account_email}</span>
                  </div>
                  <div style={styles.credRow}>
                    <span style={styles.credLabel}>Password</span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <span style={{ ...styles.credValue, letterSpacing: showPassword ? 'normal' : '3px', fontFamily: showPassword ? 'monospace' : 'inherit' }}>
                        {showPassword ? credentials.account_password : '••••••••••••'}
                      </span>
                      <button onClick={() => setShowPassword(v => !v)}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 16 }}>
                        {showPassword ? '🙈' : '👁'}
                      </button>
                      <button
                        onClick={() => { navigator.clipboard.writeText(credentials.account_password); }}
                        style={{ background: '#eef1ff', border: 'none', borderRadius: 6, padding: '3px 10px', fontSize: 11, cursor: 'pointer', color: '#4f6ef7', fontWeight: 600 }}>
                        Copy
                      </button>
                    </div>
                  </div>
                  {credentials.account_username && (
                    <div style={styles.credRow}>
                      <span style={styles.credLabel}>Username</span>
                      <span style={styles.credValue}>{credentials.account_username}</span>
                    </div>
                  )}
                  <div style={{ marginTop: 10, padding: '8px 12px', background: '#fef3c7', borderRadius: 8, fontSize: 12, color: '#92400e' }}>
                    ⚠️ These credentials are encrypted. Access is logged. Do not share outside the team.
                  </div>
                </div>
              ) : (
                <div style={{ background: '#f8f9fc', borderRadius: 10, padding: '14px 16px', fontSize: 13, color: '#9099b4', textAlign: 'center' }}>
                  Click "Reveal Credentials" to decrypt and view the login details. This action is logged.
                </div>
              )}
            </div>

            {/* Status update */}
            <div style={{ borderTop: '1px solid #f0f2f8', paddingTop: 20, marginTop: 4 }}>
              <strong style={{ fontSize: 14, color: '#1a1f5e', display: 'block', marginBottom: 14 }}>Update Account Status</strong>
              <Alert type="error" message={modalError} />
              <form onSubmit={saveStatus}>
                <FormSelect
                  label="Status"
                  options={STATUS_OPTS}
                  value={statusForm.status}
                  onChange={e => setStatusForm(f => ({ ...f, status: e.target.value }))}
                />
                <div style={{ marginBottom: 16 }}>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#2e3456', marginBottom: 6 }}>Admin Note (visible to user)</label>
                  <textarea
                    placeholder="Optional note for the user about this status change…"
                    value={statusForm.admin_note}
                    onChange={e => setStatusForm(f => ({ ...f, admin_note: e.target.value }))}
                    rows={2}
                    style={{ width: '100%', padding: '10px 14px', border: '1.5px solid #e2e6f0', borderRadius: 10, fontSize: 14, resize: 'none', fontFamily: 'inherit', boxSizing: 'border-box', outline: 'none' }}
                  />
                </div>
                <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                  <Btn variant="outline" type="button" onClick={() => setViewItem(null)}>Close</Btn>
                  <Btn loading={saving} type="submit">Save Status</Btn>
                </div>
              </form>
            </div>
          </>
        )}
      </Modal>
    </div>
  );
}

function DetailRow({ label, value, fullWidth }) {
  return (
    <div style={{ gridColumn: fullWidth ? '1 / -1' : 'auto', display: 'flex', flexDirection: 'column', gap: 2 }}>
      <span style={{ fontSize: 11, fontWeight: 700, color: '#9099b4', textTransform: 'uppercase', letterSpacing: '.4px' }}>{label}</span>
      <span style={{ fontSize: 14, color: '#1a1f5e', fontWeight: 500 }}>{value}</span>
    </div>
  );
}

const PLATFORM_COLORS = {
  'Appen':'#ff6b35','Scale AI':'#4f6ef7','Remotasks':'#16a34a','Toloka':'#f4a61d',
  'Prolific':'#7c3aed','Outlier AI':'#0891b2','DataAnnotation':'#dc2626',
};

const styles = {
  wrap:         { padding: '32px', fontFamily: "'DM Sans',sans-serif" },
  header:       { marginBottom: 24 },
  h1:           { fontFamily: "'Sora',sans-serif", fontSize: 22, fontWeight: 700, color: '#1a1f5e', margin: 0 },
  sub:          { fontSize: 14, color: '#9099b4', marginTop: 4 },
  summaryBar:   { display: 'flex', gap: 10, marginBottom: 20, flexWrap: 'wrap' },
  summaryChip:  { display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', borderRadius: 20, border: '1.5px solid', fontSize: 13, fontWeight: 500, transition: 'all .15s', userSelect: 'none' },
  filterBtn:    { padding: '6px 14px', borderRadius: 20, border: '1.5px solid #e2e6f0', background: '#fff', fontSize: 12, fontWeight: 500, cursor: 'pointer', color: '#636b89', transition: 'all .15s' },
  filterActive: { background: '#eef1ff', borderColor: '#4f6ef7', color: '#4f6ef7', fontWeight: 700 },
  table:        { width: '100%', borderCollapse: 'collapse', minWidth: 780 },
  th:           { background: '#f8f9fc', padding: '11px 14px', textAlign: 'left', fontSize: 12, fontWeight: 700, color: '#9099b4', textTransform: 'uppercase', letterSpacing: '.4px', borderBottom: '1px solid #e2e6f0' },
  tr:           { borderBottom: '1px solid #f0f2f8' },
  td:           { padding: '13px 14px', fontSize: 14, verticalAlign: 'middle' },
  pagination:   { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 20, paddingTop: 16 },
  pageBtn:      { padding: '7px 16px', border: '1.5px solid #e2e6f0', borderRadius: 8, background: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer', color: '#636b89' },
  detailsGrid:  { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 20 },
  credsSection: { background: '#f8f9fc', borderRadius: 14, padding: '16px', marginBottom: 20, border: '1px solid #e2e6f0' },
  credBox:      { background: '#fff', borderRadius: 10, border: '1px solid #e2e6f0', overflow: 'hidden' },
  credRow:      { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '11px 14px', borderBottom: '1px solid #f0f2f8', gap: 12 },
  credLabel:    { fontSize: 12, fontWeight: 700, color: '#9099b4', minWidth: 80 },
  credValue:    { fontSize: 14, color: '#1a1f5e', fontWeight: 600 },
};
